import * as React from 'react';
import {applyContainerQuery} from 'react-container-query';
import {connect} from 'react-redux';

import PropTypes from 'prop-types';

import {Session} from 'nutshell-core/session';

import {InlineTab} from 'shells/tabs/inline-tabs/inline-tab';
import {InlineTabs} from 'shells/tabs/inline-tabs/inline-tabs';
import {VIEW_ACTIVITIES_TOUR_SIDEBAR_ID} from 'shells/tours/tours';

import * as actions from '../../dashboard-actions';
import {getComponentForPaneName} from '../../dashboard-component-map';
import {FEED, ACTIVITIES} from '../../dashboard-constants';
import * as selectors from '../../dashboard-selectors';
import {getIsRequesting as getIsRequestingActivities} from '../../sidebar/activities/activities-selectors';
import {getIsRequesting as getIsRequestingTodos} from '../../sidebar/todos/todos-selectors';

import {DashboardTabs} from '../dashboard-tabs';

import styles from './dashboard-pane-right.module.css';

const MEDIUM_TIMELINE_BREAKPOINT = 1332; // 650px activities, 32 px divider, 650px timeline
const WIDE_TIMELINE_BREAKPOINT = 1632; // 800px activities, 32 px divider, 800px timeline

/*------------------------------------------------------------------------------
Presentational Component
------------------------------------------------------------------------------*/
const containerQuery = {
    'one-column': {
        maxWidth: 932,
    },
};

export class DashboardPaneRightComponent extends React.Component {
    static propTypes = {
        activeView: PropTypes.string.isRequired,
        isSidebarTwoColumns: PropTypes.bool.isRequired,
        containerQuery: PropTypes.object,

        /**
         * `true` if the active-container content should be blocked from
         * interaction (and possibly visuals), e.g., when app state is not fully
         * set up and is waiting on some asynchronous actions, or `false` to render
         * the real active-content components.
         */
        shouldBlockUI: PropTypes.bool,

        onActiveViewChange: PropTypes.func.isRequired,
        isRequestingActivities: PropTypes.bool,
        isRequestingTodos: PropTypes.bool,
        rightPaneWidth: PropTypes.number.isRequired,
        onRightPaneColumnCountChange: PropTypes.func.isRequired,
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        const isGoingFrom1to2 =
            nextProps.containerQuery &&
            !nextProps.containerQuery['one-column'] &&
            this.props.containerQuery &&
            this.props.containerQuery['one-column'];
        const isGoingFrom2to1 =
            nextProps.containerQuery &&
            nextProps.containerQuery['one-column'] &&
            this.props.containerQuery &&
            !this.props.containerQuery['one-column'];

        if (isGoingFrom1to2) {
            this.props.onRightPaneColumnCountChange(2);
            /*
             * If the sidebar is changing from one column to two columns and the feed
             * was selected, we need to change to another view to avoid having two feeds
             * (one in each column).  We'll arbitrarily change to Activities.
             */
            if (this.props.activeView === FEED) {
                this.props.onActiveViewChange(ACTIVITIES);
            }
        } else if (isGoingFrom2to1) {
            this.props.onRightPaneColumnCountChange(1);
        }
    }

    /*
     * If we need to change the active view from the feed to activities,
     * we can't render on this pass.  That's because if we _do_ continue to
     * update, the feed will be rendered in both the first and second columns
     * very briefly until the active view change is dispatched to redux.
     * That results in duplicate requests and all kinds of nasty behavior, so
     * we will prevent it here by stopping the update until the next cycle.
     */
    shouldComponentUpdate(nextProps) {
        if (
            nextProps.containerQuery &&
            !nextProps.containerQuery['one-column'] &&
            this.props.containerQuery &&
            this.props.containerQuery['one-column'] &&
            this.props.activeView === FEED
        ) {
            return false;
        }

        return true;
    }

    render() {
        const {rightPaneWidth, isSidebarTwoColumns} = this.props;
        let colTwoStyleName;
        if (rightPaneWidth < MEDIUM_TIMELINE_BREAKPOINT) {
            colTwoStyleName = styles['col-2-container'];
        } else if (
            rightPaneWidth >= MEDIUM_TIMELINE_BREAKPOINT &&
            rightPaneWidth < WIDE_TIMELINE_BREAKPOINT
        ) {
            colTwoStyleName = styles['col-2-container--medium'];
        } else {
            colTwoStyleName = styles['col-2-container--wide'];
        }

        if (!isSidebarTwoColumns) {
            return this.renderMainColumn(true);
        } else {
            return (
                <div className={styles['two-column-container']}>
                    {this.renderMainColumn(false)}
                    <div className={colTwoStyleName}>
                        <InlineTabs name='feed' selectedTab='nothing' onChange={() => {}}>
                            <InlineTab disabled={true} value={FEED}>
                                Timeline
                            </InlineTab>
                        </InlineTabs>
                        {this.renderActiveView(FEED)}
                    </div>
                </div>
            );
        }
    }

    renderMainColumn = (isOnlyColumn) => {
        return (
            <div
                id={VIEW_ACTIVITIES_TOUR_SIDEBAR_ID}
                className={isOnlyColumn ? styles['sidebar-container'] : styles['col-1-container']}
            >
                <DashboardTabs
                    name='dashboardControls'
                    activeView={this.props.activeView}
                    includeFeed={isOnlyColumn}
                    isRequestingActivities={this.props.isRequestingActivities}
                    isRequestingTodos={this.props.isRequestingTodos}
                    onChange={this.props.onActiveViewChange}
                />
                <div className={styles['active-container']}>
                    <div className={styles.active}>
                        {this.props.shouldBlockUI
                            ? undefined
                            : this.renderActiveView(this.props.activeView)}
                    </div>
                </div>
            </div>
        );
    };

    renderActiveView(activeView) {
        const ActiveView = getComponentForPaneName(activeView);

        return <ActiveView />;
    }
}

/*------------------------------------------------------------------------------
Container Component
------------------------------------------------------------------------------*/
const mapStateToProps = function (state) {
    return {
        activeView: selectors.getRightPaneActiveView(state),
        isSidebarTwoColumns: selectors.getIsSidebarTwoColumns(state),
        // Block our component content UI if we do not have a user ID from our
        // app session state.
        shouldBlockUI: !Session.getSessionStateUserId(state),
        isRequestingActivities: getIsRequestingActivities(state),
        isRequestingTodos: getIsRequestingTodos(state),
        rightPaneWidth: selectors.getRightPaneWidth(state),
    };
};

const mapDispatchToProps = function (dispatch) {
    return {
        onActiveViewChange: (activeView) => {
            dispatch(actions.changeRightPaneActiveView(activeView));
        },
        onRightPaneColumnCountChange: (colCount) => {
            dispatch(actions.changeRightPaneColumnCount(colCount));
        },
    };
};

export const DashboardPaneRight = connect(
    mapStateToProps,
    mapDispatchToProps
)(applyContainerQuery(DashboardPaneRightComponent, containerQuery));
